<template>
  <div v-show="visible" class="tab-pane-content">
    <!-- Table -->
    <data-table
      ref="dataTableRef"
      :config="tableConfig"
      :endpoint="endpoint"
      :auto-load="false"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError"
      @edit-click="handleGridEditClick"
      @button-click="handleGridButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { useCliente } from '../../composables/useCliente';

type ClienteId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface TabArquivadosProps {
  visible?: boolean;
}

const props = withDefaults(defineProps<TabArquivadosProps>(), {
  visible: false
});

const emit = defineEmits<{
  'update:total': [total: number];
}>();

const { t } = useI18n();
const { fetchCliente, updateClientePut } = useCliente();

const dataTableRef = ref<DataTableInstance | null>(null);

// Endpoint
const endpoint = computed(() => '/index.php/gestao_clientes/carrega_clientes_arquivados');

// DataTable config
const tableConfig = computed(() => {
  return {
    tableId: 'tabelaClientesArquivados',
    title: t('Clientes Arquivados'),
    filename: `clientes_arquivados_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pagination: true,
    pageLength: 25,
    heightMode: 'fill-viewport',
    rowSelection: 'single'
  };
});

const filterConfig = computed(() => {
  return {
    columns: {
      'cliente_id': 'text',
      'franquia': 'select',
      'nome': 'text',
      'bairro': 'text',
      'localidade': 'select'
    }
  };
});

const refreshButtonConfig = computed(() => ({ show: true, text: t('Atualizar') }));
const csvButtonConfig = computed(() => ({ show: true, text: 'CSV', loading: false }));

const tableColumns = computed(() => {
  return [
    {
      data: 'cliente_id',
      title: 'ID',
      width: 80,
      resizable: true,
      sortable: true,
      renderType: 'custom_clickable_id',
      clickCallback: (value: ClienteId | undefined, row: { cliente_id?: ClienteId } | undefined) => {
        if (value) {
          eventBus.emit('cliente.edit', value);
        } else if (row?.cliente_id) {
          eventBus.emit('cliente.edit', row.cliente_id);
        }
      },
      dataType: 'number',
    },
    {
      data: 'franquia',
      title: t('Franquia', 'Franquia'),
      width: 150,
      resizable: true,
      sortable: true,
    },
    {
      data: 'nome',
      title: t('Nome', 'Nome'),
      width: 220,
      resizable: true,
      sortable: true,
    },
    {
      data: 'bairro',
      title: t('Bairro', 'Bairro'),
      width: 140,
      resizable: true,
      sortable: true,
    },
    {
      data: 'localidade',
      title: t('Localidade', 'Localidade'),
      width: 160,
      resizable: true,
      sortable: true,
    },
    {
      title: t('Ações', 'Ações'),
      width: 140,
      renderType: 'clickable_button',
      buttonText: t('Desarquivar', 'Desarquivar'),
      buttonIcon: 'pi pi-replay',
      buttonClass: 'p-button p-button-primary p-button-sm',
      buttonAction: 'desarquivar',
      buttonCondition: (row: { cliente_id?: ClienteId }) => !!row.cliente_id,
    },
  ];
});

const loadData = (): void => {
  log('GestaoClientesTabClientesArquivados', 'loadData', 'Triggering grid refresh');
  dataTableRef.value?.loadData();
};

const handleDataLoaded = (data: unknown[]): void => {
  log('GestaoClientesTabClientesArquivados', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  emit('update:total', data.length || 0);
};

const handleDataError = (err: { message?: string } | Error): void => {
  log('GestaoClientesTabClientesArquivados', 'handleDataError', 'Error loading data', err);
  const message = err instanceof Error ? err.message : err.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar clientes arquivados: ') + message);
};

const handleGridEditClick = (clienteId: ClienteId): void => {
  log('GestaoClientesTabClientesArquivados', 'handleGridEditClick', 'Edit clicked from grid', clienteId);
  handleEditarCliente(clienteId);
};

const handleGridButtonClick = ({ value, data }: { value?: ClienteId; data?: { cliente_id?: ClienteId } }): void => {
  log('GestaoClientesTabClientesArquivados', 'handleGridButtonClick', 'Button clicked', { value });
  handleDesarquivarCliente(value || data?.cliente_id);
};

const handleEditarCliente = (clienteId?: ClienteId): void => {
  log('GestaoClientesTabClientesArquivados', 'handleEditarCliente', 'Opening edit cliente modal', clienteId);
  if (clienteId) eventBus.emit('cliente.edit', clienteId);
};

const handleDesarquivarCliente = async (clienteId?: ClienteId): Promise<void> => {
  log('GestaoClientesTabClientesArquivados', 'handleDesarquivarCliente', 'Desarquivando cliente', clienteId);
  if (!clienteId) return;

  try {
    log('GestaoClientesTabClientesArquivados', 'handleDesarquivarCliente', 'Loading client data', clienteId);
    const cliente = await fetchCliente(Number(clienteId));

    (cliente as { cliente_status?: number }).cliente_status = 1;

    log('GestaoClientesTabClientesArquivados', 'handleDesarquivarCliente', 'Updating client status to active', cliente);

    await updateClientePut(cliente);

    log('GestaoClientesTabClientesArquivados', 'handleDesarquivarCliente', 'Client desarquivado successfully');

    eventBus.emit('globalMessage.success', t('Cliente desarquivado com sucesso!'));

    loadData();

    eventBus.emit('cliente.saved');
  } catch (err) {
    log('GestaoClientesTabClientesArquivados', 'handleDesarquivarCliente', 'Exception during desarquivar', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao desarquivar cliente: ') + message);
  }
};

const handleClienteSaved = (): void => {
  if (props.visible) loadData();
};

const handleClienteDesarquivado = (): void => {
  if (props.visible) loadData();
};

onMounted(() => {
  log('GestaoClientesTabClientesArquivados', 'onMounted', 'Component mounted');

  eventBus.on('cliente.saved', handleClienteSaved);
  eventBus.on('cliente.desarquivado', handleClienteDesarquivado);
});

onBeforeUnmount(() => {
  log('GestaoClientesTabClientesArquivados', 'onBeforeUnmount', 'Cleaning up listeners');
  eventBus.off('cliente.saved', handleClienteSaved);
  eventBus.off('cliente.desarquivado', handleClienteDesarquivado);
});

defineExpose({
  loadData
});
</script>

<style scoped>
.tab-pane-content {
  padding: 20px;
}
</style>

